import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import {
  BRAND_IDENTITY_EVENT,
  getActiveEntitySlug,
  getCurrentBrand,
  rememberEntryEntitySlug,
  setActiveEntityBrand,
} from './activeEntity';

/**
 * What a user sees as "the app": its name, tagline, logo, browser-tab icon and
 * legal name. A company's users see their company (Admin → Brands → Identity);
 * RiskLo itself only shows as "Powered by RiskLo". With no brand known (signed
 * out, no sign-in link) it is the RiskLo home brand.
 */
export const PLATFORM_IDENTITY = Object.freeze({
  appName: 'RiskLo',
  tagline: 'Trading Risk Platform',
  logoUrl: '/RiskLo_LogoOnly.png',
  faviconUrl: '/favicon.png',
  legalName: 'RiskLo',
  supportEmail: null,
  isPlatform: true,
  plan: null,
  workspace: null,
  poweredBy: false,
  checkoutUrl: null,
});

/**
 * The brand's company plan (Admin → Brands → Plan):
 *   starter / brand  the app is RiskLo; the company shows as the workspace
 *                    (Brand also gets its colors and layout style).
 *   partner          brand-first: the company's name and logo lead the app
 *                    ("Vector Overview"), RiskLo shows as "Powered by RiskLo".
 *   whitelabel       the app is the company (its own app name); RiskLo only as
 *                    "Powered by RiskLo".
 */
export function getBrandPlan(brand = getCurrentBrand()) {
  const p = String(brand?.branding?.plan || brand?.plan || '').toLowerCase();
  return ['brand', 'partner', 'whitelabel'].includes(p) ? p : 'starter';
}

/** Partner and White-label: the company leads the app and RiskLo shows as "Powered by RiskLo". */
export function isBrandFirstPlan(plan) {
  return plan === 'partner' || plan === 'whitelabel';
}

export function getBrandIdentity() {
  const brand = getCurrentBrand();
  if (!brand) return PLATFORM_IDENTITY;
  const b = brand.branding || {};
  const slug = String(brand.slug || '').toLowerCase();
  if (slug === 'risklo') return PLATFORM_IDENTITY;
  const plan = getBrandPlan(brand);
  const companyName = String(brand.displayName || '').trim();
  if (plan === 'partner') {
    // Brand-first: the company leads (name, logo, browser tab); RiskLo is the platform underneath.
    return {
      appName: companyName || PLATFORM_IDENTITY.appName,
      tagline: 'Powered by RiskLo',
      logoUrl: b.logoUrl || PLATFORM_IDENTITY.logoUrl,
      faviconUrl: b.logoUrl || PLATFORM_IDENTITY.faviconUrl,
      legalName: PLATFORM_IDENTITY.legalName,
      supportEmail: b.supportEmail || null,
      isPlatform: false,
      plan,
      workspace: null,
      poweredBy: true,
      checkoutUrl: b.checkoutUrl || null,
    };
  }
  if (plan !== 'whitelabel') {
    // RiskLo is the app; the company is the workspace people are in.
    return {
      ...PLATFORM_IDENTITY,
      plan,
      workspace: companyName ? { name: companyName, logoUrl: b.logoUrl || null, accent: b.accentColor || null } : null,
    };
  }
  const appName = String(b.appName || companyName).trim() || PLATFORM_IDENTITY.appName;
  return {
    appName,
    tagline: b.tagline || '',
    logoUrl: b.logoUrl || PLATFORM_IDENTITY.logoUrl,
    faviconUrl: b.faviconUrl || b.logoUrl || PLATFORM_IDENTITY.faviconUrl,
    legalName: b.legalName || appName,
    supportEmail: b.supportEmail || null,
    isPlatform: false,
    plan,
    workspace: null,
    poweredBy: true,
    checkoutUrl: b.checkoutUrl || null,
  };
}

/** Browser tab: the brand's name and icon instead of RiskLo's ("Vector · Overview" when a page is known). */
export function applyBrandIdentity(identity = getBrandIdentity(), pageLabel = null) {
  try {
    if (pageLabel && !identity.isPlatform) document.title = `${identity.appName} · ${pageLabel}`;
    else document.title = identity.tagline ? `${identity.appName} - ${identity.tagline}` : identity.appName;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = identity.faviconUrl;
  } catch { /* no document (tests) */ }
}

/** The brand identity, kept current when the brand or its look changes. */
export function useBrandIdentity() {
  const [identity, setIdentity] = useState(getBrandIdentity);
  useEffect(() => {
    const refresh = () => {
      const next = getBrandIdentity();
      setIdentity(next);
      applyBrandIdentity(next);
    };
    window.addEventListener(BRAND_IDENTITY_EVENT, refresh);
    return () => window.removeEventListener(BRAND_IDENTITY_EVENT, refresh);
  }, []);
  return identity;
}

/**
 * A company's sign-in link: risklo.io/b/<slug> or risklo.io/?brand=<slug>. Picks
 * that brand before sign-in and loads its public look, so the login page already
 * shows the company. Returns the slug, or null when the URL has none.
 */
/**
 * Partner short links: risklo.io/<slug> (e.g. risklo.io/vector). The app checks
 * a one-segment path that isn't one of its own pages against the brand list.
 */
export async function loadVanityBrand(slug) {
  const s = String(slug || '').toLowerCase();
  if (!/^[a-z0-9][a-z0-9_-]{0,62}$/.test(s)) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/api/entities/public/${encodeURIComponent(s)}?vanity=1`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success || !data.brand) return null;
    const { slug: found, displayName, ...branding } = data.brand;
    rememberEntryEntitySlug(found);
    setActiveEntityBrand({ slug: found, displayName, branding });
    return found;
  } catch {
    return null;
  }
}

export function readEntryBrandSlug(location = window.location) {
  const path = location.pathname.match(/^\/b\/([a-z0-9][a-z0-9_-]{0,62})\/?$/i);
  if (path) return path[1].toLowerCase();
  const query = new URLSearchParams(location.search).get('brand');
  return query && /^[a-z0-9][a-z0-9_-]{0,62}$/i.test(query) ? query.toLowerCase() : null;
}

export async function applyEntryBrandLink() {
  const slug = readEntryBrandSlug();
  if (!slug) return null;
  // Tidy the address bar; the app routes from its own state, not the path.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('brand');
    const path = /^\/b\//i.test(url.pathname) ? '/' : url.pathname;
    window.history.replaceState(null, '', `${path}${url.search}${url.hash}`);
  } catch { /* old browsers */ }
  const already = getActiveEntitySlug() === slug && getCurrentBrand();
  rememberEntryEntitySlug(slug);
  if (already) return slug;
  try {
    const res = await fetch(`${API_BASE_URL}/api/entities/public/${encodeURIComponent(slug)}`);
    const data = await res.json();
    if (!res.ok || !data.success || !data.brand) return null;
    const { slug: s, displayName, ...branding } = data.brand;
    setActiveEntityBrand({ slug: s, displayName, branding });
    return slug;
  } catch {
    return null;
  }
}
